<template>
  <PageChrome>
    <template #title>Users</template>

    <template #actions>
      <label style="display:inline-flex; align-items:center; gap:8px; font-size:12px; color: var(--muted);">
        <input type="checkbox" v-model="includeDeleted" @change="load" />
        Show deleted
      </label>
      <button v-if="!isDialerOnly" class="btn" @click="openCreate">Add user</button>
    </template>

    <div v-if="error" class="error">{{ error }}</div>

    <div class="list-panel">
      <div class="list-panel-header">
        <div class="list-panel-title">Users <span class="muted">({{ users.length }})</span></div>
      </div>

      <div v-if="loading" class="list-panel-empty">Loading…</div>
      <div v-else-if="users.length === 0" class="list-panel-empty">No users.</div>

      <div v-else class="tableWrap">
        <table class="table">
          <thead>
            <tr>
              <th>Email</th>
              <th style="width: 160px;">First name</th>
              <th style="width: 160px;">Last name</th>
              <th style="width: 110px;">Role</th>
              <th style="width: 110px;">Status</th>
              <th style="width: 210px; text-align:right;">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="u in users" :key="u.id" :class="u.deleted_at ? 'rowDeleted' : ''">
              <td class="mono">{{ u.email }}</td>
              <td>{{ u.first_name || '—' }}</td>
              <td>{{ u.last_name || '—' }}</td>
              <td>
                <span class="badge" :class="roleBadge(u.role)">{{ u.role }}</span>
              </td>
              <td>
                <span class="badge" :class="u.deleted_at ? 'danger' : 'success'">{{ u.deleted_at ? 'deleted' : 'active' }}</span>
              </td>
              <td style="text-align:right; white-space:nowrap;">
                <button class="btn" style="height:30px; padding:0 10px;" @click="openEdit(u)">Edit</button>
                <button
                  v-if="!u.deleted_at"
                  class="btn danger"
                  style="height:30px; padding:0 10px; margin-left:8px;"
                  :disabled="disableDelete(u)"
                  @click="softDelete(u)"
                >
                  Delete
                </button>
                <button
                  v-else
                  class="btn"
                  style="height:30px; padding:0 10px; margin-left:8px;"
                  @click="restore(u)"
                >
                  Restore
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="!isDialerOnly && !loading && users.length > 0" class="list-panel runtimeUserPanel">
      <div class="list-panel-header">
        <div class="list-panel-title">Agent runtime user</div>
      </div>
      <div class="runtimeUserBody">
        <div class="runtimeUserHint muted">If not configured, this will default to the earliest user.</div>
        <div class="runtimeUserControlRow">
          <div class="runtimeUserControl">
            <SearchSelect
              v-model="agentRuntimeUserId"
              :options="agentRuntimeUserOptions"
              placeholder="Select runtime user…"
              :allow-clear="true"
              clear-label="(none)"
            />
          </div>
          <button class="btn" :disabled="runtimeUserSaving" @click="saveAgentRuntimeUser">
            {{ runtimeUserSaving ? 'Saving…' : 'Save' }}
          </button>
          <span v-if="runtimeUserSaved && !runtimeUserSaving" class="saveSuccess">Saved</span>
        </div>
        <div v-if="runtimeUserError" class="error" style="margin-top:8px; margin-bottom:0;">{{ runtimeUserError }}</div>
      </div>
    </div>

    <Modal v-if="showCreate" title="Add user" @close="closeCreate">
      <div class="form">
        <div class="field">
          <label>Email</label>
          <input class="input" v-model="createDraft.email" placeholder="you@company.com" />
        </div>

        <div class="grid">
          <div class="field">
            <label>First name</label>
            <input class="input" v-model="createDraft.first_name" placeholder="First" />
          </div>
          <div class="field">
            <label>Last name</label>
            <input class="input" v-model="createDraft.last_name" placeholder="Last" />
          </div>
        </div>

        <div class="field">
          <label>Role</label>
          <select class="input" v-model="createDraft.role">
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div class="field">
          <label>Password</label>
          <input class="input" type="password" v-model="createDraft.password" placeholder="••••••••" />
        </div>

        <div v-if="createError" class="error">{{ createError }}</div>
      </div>

      <template #footer>
        <button class="btn" @click="closeCreate">Cancel</button>
        <button class="btn primary" :disabled="createLoading" @click="createUser">{{ createLoading ? 'Creating…' : 'Create' }}</button>
      </template>
    </Modal>

    <Modal v-if="showEdit" title="Edit user" modal-overflow="visible" body-overflow="visible" @close="closeEdit">
      <div class="form">
        <div class="field">
          <label>Email</label>
          <div class="mono muted">{{ editTarget?.email }}</div>
        </div>

        <div class="grid">
          <div class="field">
            <label>First name</label>
            <input class="input" v-model="editDraft.first_name" placeholder="First" />
          </div>
          <div class="field">
            <label>Last name</label>
            <input class="input" v-model="editDraft.last_name" placeholder="Last" />
          </div>
        </div>

        <div class="field">
          <label>Role</label>
          <select class="input" v-model="editDraft.role" :disabled="roleLocked">
            <option value="member">Member</option>
            <option value="admin">Admin</option>
            <option v-if="editTarget?.role === 'owner'" value="owner">Owner</option>
          </select>
          <div v-if="roleLocked" class="muted" style="font-size:12px; margin-top:6px;">Role cannot be changed for this user.</div>
        </div>

        <div v-if="!isDialerOnly" class="field">
          <label>Account owner field</label>
          <SearchSelect
            v-model="editDraft.sf_account_field"
            :options="sfAccountFieldOptions"
            placeholder="Select field…"
            :allow-clear="true"
            clear-label="(none)"
          />
          <div class="muted" style="font-size:12px; margin-top:4px;">
            Salesforce Account field used for the "My Accounts" view filter
          </div>
        </div>

        <!-- Contact/Lead owner fields removed — prospecting ownership
             is now configured at the workspace level (Integrations → Field mappings).
             Per-user config is for "My Accounts" viewing only (account-level). -->

        <div class="field">
          <label>Set new password (optional)</label>
          <input class="input" type="password" v-model="editDraft.password" placeholder="Leave blank to keep" />
        </div>

        <div v-if="editError" class="error">{{ editError }}</div>
      </div>

      <template #footer>
        <button class="btn" @click="closeEdit">Cancel</button>
        <button class="btn primary" :disabled="editLoading" @click="saveEdit">{{ editLoading ? 'Saving…' : 'Save' }}</button>
      </template>
    </Modal>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import Modal from '../../components/Modal.vue'
import SearchSelect from '../../components/SearchSelect.vue'
import { apiFetch } from '../../services/api'
import { getSalesforceFieldIconKey } from '../../services/iconRegistry'
import { useChatScope } from '../composables/useChatScope'
import { useCurrentUser } from '../../services/user'

const { isDialerOnly } = useCurrentUser()

type UserListResponse = {
  users: UserRow[],
  agent_runtime_user_id?: string
}

type UserRow = {
  id: string,
  email: string,
  first_name?: string,
  last_name?: string,
  role: string,
  owner_field_name?: string,
  owner_field_config?: Record<string, Record<string, string>>,
  deleted_at?: string,
  created_at?: string
}

const router = useRouter()
const { setScope, clearScope } = useChatScope()

const loading = ref(false)
const error = ref('')
const includeDeleted = ref(false)
const users = ref<UserRow[]>([])

const me = ref(null)

const showCreate = ref(false)
const createDraft = ref({ email: '', first_name: '', last_name: '', role: 'member', password: '' })
const createLoading = ref(false)
const createError = ref('')

const showEdit = ref(false)
const editTarget = ref(null)
const editDraft = ref({ first_name: '', last_name: '', role: 'member', sf_account_field: '', sf_contact_field: '', sf_lead_field: '', password: '' })
const editLoading = ref(false)
const editError = ref('')

const sfAccountFieldOptions = ref([])
const sfContactFieldOptions = ref([])
const sfLeadFieldOptions = ref([])

const agentRuntimeUserId = ref('')
const runtimeUserSaving = ref(false)
const runtimeUserError = ref('')
const runtimeUserSaved = ref(false)
let runtimeUserSavedTimer = null

const roleLocked = computed(() => {
  const t = editTarget.value
  if (!t) return true
  if (String(t.role) === 'owner') return true
  if (String(t.id) && String(t.id) === String(me.value?.user_id || '')) return true
  return false
})

const agentRuntimeUserOptions = computed(() => {
  return (users.value || [])
    .filter((u) => !u?.deleted_at)
    .map((u) => ({
      value: String(u.id || ''),
      label: `${String(u.first_name || '').trim()} ${String(u.last_name || '').trim()}`.trim() || String(u.email || '').trim() || 'User',
    }))
    .filter((opt) => opt.value)
})

function roleBadge(role?: string): string {
  const r = (role || '').toLowerCase()
  if (r === 'owner') return 'success'
  if (r === 'admin') return 'warning'
  return ''
}

function disableDelete(u) {
  if (!u) return true
  const id = String(u.id || '')
  if (id && id === String(me.value?.user_id || '')) return true
  if (String(u.role || '').toLowerCase() === 'owner') return true
  return false
}

async function loadMe() {
  try {
    me.value = await apiFetch('/auth/me')
  } catch {
    me.value = null
  }
}

async function loadOwnerFieldOptions() {
  const mapFields = (resp) => {
    const fields = Array.isArray(resp?.fields) ? resp.fields : (Array.isArray(resp) ? resp : [])
    return fields.map(f => ({
      value: f.name || '',
      label: f.label || f.name || '',
      iconKey: getSalesforceFieldIconKey(f.type),
    }))
  }
  try {
    const acct = await apiFetch('/settings/integrations/salesforce/user_lookup_fields?object_name=Account').catch(() => [])
    sfAccountFieldOptions.value = mapFields(acct)
  } catch (e) {
    console.error('Failed to load owner field options:', e)
  }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const resp = await apiFetch<UserListResponse>(`/settings/users?include_deleted=${includeDeleted.value ? '1' : '0'}`)
    users.value = Array.isArray(resp?.users) ? resp.users : []
    agentRuntimeUserId.value = String(resp?.agent_runtime_user_id || '').trim()
  } catch (e) {
    const msg = e?.message || 'Failed to load users'
    error.value = msg
    users.value = []
    agentRuntimeUserId.value = ''
    if (Number(e?.status || 0) === 403) {
      router.push('/home')
    }
  } finally {
    loading.value = false
  }
}

function openCreate() {
  showCreate.value = true
  createDraft.value = { email: '', first_name: '', last_name: '', role: 'member', password: '' }
  createError.value = ''
}

function closeCreate() {
  showCreate.value = false
  createError.value = ''
}

async function createUser() {
  createLoading.value = true
  createError.value = ''
  try {
    const payload = {
      email: createDraft.value.email,
      first_name: createDraft.value.first_name,
      last_name: createDraft.value.last_name,
      role: createDraft.value.role,
      password: createDraft.value.password,
    }
    await apiFetch('/settings/users', { method: 'POST', body: payload })
    showCreate.value = false
    await load()
  } catch (e) {
    createError.value = e?.message || 'Failed to create user'
  } finally {
    createLoading.value = false
  }
}

function openEdit(u) {
  editTarget.value = u
  const sfConfig = u?.owner_field_config?.salesforce || {}
  editDraft.value = {
    first_name: u?.first_name || '',
    last_name: u?.last_name || '',
    role: u?.role || 'member',
    sf_account_field: sfConfig.account || u?.owner_field_name || '',
    sf_contact_field: sfConfig.contact || '',
    sf_lead_field: sfConfig.lead || '',
    password: '',
  }
  editError.value = ''
  showEdit.value = true
}

function closeEdit() {
  showEdit.value = false
  editTarget.value = null
  editError.value = ''
}

async function saveEdit() {
  const t = editTarget.value
  if (!t?.id) return
  editLoading.value = true
  editError.value = ''
  try {
    const pw = (editDraft.value.password || '').trim()
    const sfConfig: Record<string, string> = {}
    if (editDraft.value.sf_account_field) sfConfig.account = editDraft.value.sf_account_field
    if (editDraft.value.sf_contact_field) sfConfig.contact = editDraft.value.sf_contact_field
    if (editDraft.value.sf_lead_field) sfConfig.lead = editDraft.value.sf_lead_field
    const ownerFieldConfig = Object.keys(sfConfig).length ? { salesforce: sfConfig } : null
    const payload = {
      first_name: editDraft.value.first_name,
      last_name: editDraft.value.last_name,
      owner_field_config: ownerFieldConfig,
      password: !roleLocked.value && pw ? pw : null,
      role: !roleLocked.value ? editDraft.value.role : null
    }

    await apiFetch(`/settings/users/${encodeURIComponent(t.id)}`, { method: 'PUT', body: payload })
    showEdit.value = false
    await load()
  } catch (e) {
    editError.value = e?.message || 'Failed to update user'
  } finally {
    editLoading.value = false
  }
}

async function softDelete(u: UserRow) {
  if (!u?.id) return
  try {
    await apiFetch(`/settings/users/${encodeURIComponent(u.id)}`, { method: 'PUT', body: { deleted: true } })
    await load()
  } catch (e) {
    error.value = e?.message || 'Failed to delete user'
  }
}

async function restore(u: UserRow) {
  if (!u?.id) return
  try {
    await apiFetch(`/settings/users/${encodeURIComponent(u.id)}`, { method: 'PUT', body: { deleted: false } })
    await load()
  } catch (e) {
    error.value = e?.message || 'Failed to restore user'
  }
}

async function saveAgentRuntimeUser() {
  runtimeUserSaving.value = true
  runtimeUserError.value = ''
  runtimeUserSaved.value = false
  try {
    await apiFetch('/settings/org_settings', {
      method: 'PUT',
      body: {
        ui_preferences: {
          agent_runtime_user_id: String(agentRuntimeUserId.value || '').trim() || null,
        },
      },
    })
    runtimeUserSaved.value = true
    if (runtimeUserSavedTimer) {
      clearTimeout(runtimeUserSavedTimer)
    }
    runtimeUserSavedTimer = setTimeout(() => {
      runtimeUserSaved.value = false
      runtimeUserSavedTimer = null
    }, 1800)
  } catch (e) {
    runtimeUserError.value = e?.message || 'Failed to save runtime user'
  } finally {
    runtimeUserSaving.value = false
  }
}

onMounted(async () => {
  setScope({ scopeType: 'workspace', scopeSubjectId: '', pageContext: null })
  await loadMe()
  await load()
  if (!isDialerOnly.value) await loadOwnerFieldOptions()
})

onBeforeUnmount(() => {
  clearScope()
})
</script>

<style scoped>
.error { color: var(--danger); margin-bottom: 10px; }
.muted { color: var(--muted); }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }

.tableWrap {
  width: 100%;
  overflow: auto;
}

.runtimeUserPanel {
  margin-top: 16px;
  overflow: visible;
}

.runtimeUserBody {
  padding: 16px;
  overflow: visible;
}

.runtimeUserHint {
  font-size: 13px;
  margin-bottom: 12px;
}

.runtimeUserControlRow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.runtimeUserControl {
  width: min(360px, 100%);
  max-width: 100%;
  min-width: 0;
}

.saveSuccess {
  color: #16a34a;
  font-size: 13px;
  font-weight: 600;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field label {
  font-size: 12px;
  color: var(--muted);
  font-weight: 600;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

@media (max-width: 640px) {
  .grid { grid-template-columns: 1fr; }
}

.rowDeleted {
  opacity: 0.65;
}
</style>
